import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from "@material-ui/core";

// Full traceability tree of an article — React port of the test UI's "Trace" tab.
// Data source: GET /articlesTrace/:id/trace → { article, materials[], packaging[] }.
// Each "step" = { process, type?, relativeAmount?, supplier, origin, transport,
// processAssumed, supplierAssumed, originAssumed }.

const muted = { color: "#94a3b8" };

// Amber "asumido" chip — same colour as the "Asumido" status badge in the Gaps tab (consistency).
const AssumedChip = () => (
  <span
    style={{
      background: "rgba(217,119,6,0.14)",
      color: "#d97706",
      fontSize: 9,
      fontWeight: 700,
      padding: "1px 5px",
      borderRadius: 10,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      marginLeft: 4,
      verticalAlign: "middle",
    }}
  >
    asumido
  </span>
);

// One value with an optional "asumido" chip and a fallback dash.
const LegValue = ({ value, assumed }) =>
  value ? (
    <>
      {value}
      {assumed && <AssumedChip />}
    </>
  ) : (
    <span style={muted}>—</span>
  );

// Proveedor · origen · transporte, inline with text labels (no emojis). Marks each if assumed.
const TraceLeg = ({ s }) => (
  <span>
    {s.supplier ? (
      <>
        <strong>{s.supplier}</strong>
        {s.supplierAssumed && <AssumedChip />}
      </>
    ) : (
      <span style={muted}>sin proveedor</span>
    )}
    <span style={muted}> · </span>📍 <LegValue value={s.origin} assumed={s.originAssumed} />
    <span style={muted}> · </span>🚚 <LegValue value={s.transport} assumed={s.transportAssumed} />
  </span>
);

export const TraceSteps = ({ steps, showType, showAmount }) => {
  if (!steps || !steps.length)
    return <div style={{ ...muted, fontSize: 12, padding: "4px 0" }}>— ninguno —</div>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Proceso</TableCell>
          {showType && <TableCell>Tipo</TableCell>}
          {showAmount && <TableCell>%</TableCell>}
          <TableCell>Proveedor · Origen · Transporte</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {steps.map((s, i) => {
          const assumedRow = s.processAssumed || s.supplierAssumed || s.originAssumed;
          return (
            <TableRow key={i} style={assumedRow ? { background: "rgba(251,191,36,0.06)" } : undefined}>
              <TableCell style={{ fontSize: 12 }}>
                {s.process || <span style={muted}>sin definir</span>}
                {s.processAssumed && <AssumedChip />}
              </TableCell>
              {showType && <TableCell style={{ fontSize: 12 }}>{s.type || "—"}</TableCell>}
              {showAmount && (
                <TableCell style={{ fontSize: 12 }}>
                  {s.relativeAmount != null ? (Number(s.relativeAmount) * 100).toFixed(0) + "%" : "—"}
                </TableCell>
              )}
              <TableCell style={{ fontSize: 12 }}>
                <TraceLeg s={s} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// Extractions with their linked processing(s) nested underneath (matched by `order`).
export const CompositionTree = ({ extractions, processings }) => {
  if (!extractions || !extractions.length)
    return <div style={{ ...muted, fontSize: 12, padding: "4px 0" }}>— sin extracciones —</div>;
  const key = (o) => (o == null ? "_" : String(o));
  const procByOrder = {};
  (processings || []).forEach((p) => {
    (procByOrder[key(p.order)] = procByOrder[key(p.order)] || []).push(p);
  });
  const extractionOrders = new Set(extractions.map((e) => key(e.order)));
  const orphans = Object.keys(procByOrder)
    .filter((k) => !extractionOrders.has(k))
    .flatMap((k) => procByOrder[k]);

  return (
    <div>
      {extractions.map((e, i) => {
        const procs = procByOrder[key(e.order)] || [];
        return (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {e.process || <span style={muted}>sin definir</span>}
              {e.relativeAmount != null && (
                <span style={muted}> · {(Number(e.relativeAmount) * 100).toFixed(0)}%</span>
              )}
              {e.processAssumed && <AssumedChip />}
            </div>
            <div style={{ fontSize: 12, paddingLeft: 20 }}>
              <TraceLeg s={e} />
            </div>
            {procs.length ? (
              procs.map((p, j) => (
                <div
                  key={j}
                  style={{ fontSize: 12, marginLeft: 8, paddingLeft: 12, borderLeft: "2px solid #e2e8f0", marginTop: 4 }}
                >
                  <span style={muted}>procesado: </span>
                  {p.process || "—"}
                  {p.processAssumed && <AssumedChip />}
                  <span style={muted}> — </span>
                  <TraceLeg s={p} />
                </div>
              ))
            ) : (
              <div style={{ fontSize: 11, paddingLeft: 20, ...muted }}>— sin procesado —</div>
            )}
          </div>
        );
      })}
      {orphans.map((p, j) => (
        <div key={"o" + j} style={{ fontSize: 12, paddingLeft: 20, marginTop: 4 }}>
          <span style={muted}>procesado (sin extracción ligada): </span>
          {p.process || "—"}
          {p.processAssumed && <AssumedChip />}
          <span style={muted}> — </span>
          <TraceLeg s={p} />
        </div>
      ))}
    </div>
  );
};

const Fact = ({ k, v, assumed }) => (
  <span style={{ marginRight: 16, fontSize: 12 }}>
    <span style={muted}>{k}: </span>
    {v ?? "—"}
    {assumed && <AssumedChip />}
  </span>
);
const legend = { fontWeight: 600, fontSize: 12, margin: "10px 0 4px" };
const section = { borderLeftWidth: 3, borderLeftStyle: "solid", paddingLeft: 12, borderRadius: 2 };
const sectionTitle = { fontWeight: 700, fontSize: 13, margin: "16px 0 8px", color: "#64748b" };

const ArticleTraceView = ({ api, articleId }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api("GET", `/articlesTrace/${articleId}/trace`);
      if (!alive) return;
      setD(r.ok ? r.data : null);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [api, articleId]);

  if (loading)
    return (
      <Box textAlign="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  if (!d || !d.article)
    return (
      <Box py={3} textAlign="center" style={muted}>
        Sin datos de trazabilidad
      </Box>
    );

  const a = d.article;
  // Weight consistency (Business Rule 4): article weight vs sum of material weights (>10% → flag).
  const matWeightSum = (d.materials || []).reduce((s, m) => s + (Number(m.weight) || 0), 0);
  const aWeight = Number(a.weight) || 0;
  const weightOff = aWeight > 0 && matWeightSum > 0 && Math.abs(aWeight - matWeightSum) / aWeight > 0.1;
  return (
    <Box p={2}>
      {/* ① Article */}
      <div style={{ ...section, borderLeftColor: "#2563eb", marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>① Artículo</div>
        <div style={{ lineHeight: 1.9, marginBottom: 8 }}>
          <Fact k="Nombre" v={a.name} />
          <Fact k="Ref" v={a.reference} />
          <Fact k="Categoría" v={a.category} />
          <Fact k="Color" v={a.color} />
          <Fact k="Peso (kg)" v={a.weight} />
          {matWeightSum > 0 && (
            <div style={{ fontSize: 12, color: weightOff ? "#9333ea" : "#94a3b8" }}>
              Suma pesos materiales: {matWeightSum.toFixed(3)} kg
              {weightOff && ` · desviación ${(Math.abs(aWeight - matWeightSum) / aWeight * 100).toFixed(0)}% (revisar)`}
            </div>
          )}
          <Fact k="Uds" v={a.units} />
          <Fact k="Merma mfg" v={a.manufacturingLossRate} assumed={a.manufacturingLossRateAssumed} />
          <Fact k="Peligrosas" v={a.hazardous && a.hazardous.length ? a.hazardous.join(", ") : "—"} />
        </div>
        <div style={legend}>Manufactura (Tier 1)</div>
        <TraceSteps steps={a.manufacturing} />
        <div style={legend}>Acabado de artículo (Tier 2)</div>
        <TraceSteps steps={a.finishing} showType />
      </div>

      {/* ② Materials */}
      <div style={sectionTitle}>② MATERIALES</div>
      {(d.materials || []).length ? (
        (d.materials || []).map((m, i) => (
          <div key={i} style={{ ...section, borderLeftColor: "#db2777", marginTop: i > 0 ? 12 : 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{m.name ?? "—"}</div>
            <div style={{ lineHeight: 1.9, marginBottom: 8 }}>
              <Fact k="Ref" v={m.reference} />
              <Fact k="Categoría" v={m.category} />
              <Fact k="Aplicación" v={m.application} />
              <Fact k="Peso (kg)" v={m.weight} />
              <Fact k="Origen" v={m.origin} />
              <Fact k="Merma" v={m.lossRate} assumed={m.lossRateAssumed} />
            </div>
            <div style={legend}>Composición: extracción → procesado (Tier 4 → Tier 3)</div>
            <CompositionTree extractions={m.extractions} processings={m.processings} />
            <div style={legend}>Acabado (Tier 2)</div>
            <TraceSteps steps={m.finishings} showType />
          </div>
        ))
      ) : (
        <div style={muted}>Sin materiales</div>
      )}

      {/* ③ Packaging */}
      <div style={sectionTitle}>③ PACKAGING</div>
      {d.packaging && d.packaging.length ? (
        <div style={{ ...section, borderLeftColor: "#a16207" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Share</TableCell>
                <TableCell>Take-back</TableCell>
                <TableCell>Peligrosas</TableCell>
                <TableCell>Proveedor · Origen · Transporte</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.packaging.map((p, i) => (
                <TableRow key={i}>
                  <TableCell style={{ fontSize: 12 }}>{p.name ?? "—"}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{p.type ?? "—"}</TableCell>
                  <TableCell>{p.channel ?? "—"}</TableCell>
                  <TableCell>{p.share != null ? p.share : "—"}</TableCell>
                  <TableCell>{p.takeBack ? "✓" : "—"}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{p.hazardous ?? "—"}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>
                    <TraceLeg s={p} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div style={muted}>Sin packaging</div>
      )}
    </Box>
  );
};

export default ArticleTraceView;
